import { Box } from "@mui/material";
import { GamesModuleLayout, ModuleType } from "entities/modules/types";
import {
  PageModuleDataWithPageId,
  PageModuleStatus,
} from "entities/pages/types";
import { FC } from "react";
import ModuleTileHeader from "shared/components/ModuleTileHeader";
import { ContentWrapper } from "../../styled";
import BigTilesLayout from "./components/BigTilesLayout";
import FavoriteGames from "./components/FavoriteGames";
import HorizontalLayout from "./components/HorizontalLayout";
import NumericLayout from "./components/NumericLayout";
import RecentlyPlayed from "./components/RecentlyPlayed";
import StudioFilter from "./components/StudioFilter";
import VerticalLayout from "./components/VerticalLayout";

interface GamesProps {
  isLoading: boolean;
  data: PageModuleDataWithPageId;
  onEditComponent: (data: PageModuleDataWithPageId) => void;
  onDeleteComponent: (data: PageModuleDataWithPageId) => void;
  onUpdateStatus: (params: {
    moduleId: string;
    status: PageModuleStatus;
  }) => void;
  isNewlyGenerated?: boolean;
}

interface LayoutProps {
  gameIds: string[];
  isLoading: boolean;
  isStacked?: boolean;
  isNewlyGenerated?: boolean;
}

const LAYOUT_COMPONENTS: Record<
  string,
  {
    Component: React.ComponentType<LayoutProps>;
    props: Partial<LayoutProps>;
  }
> = {
  [GamesModuleLayout.VerticalTiles]: {
    Component: VerticalLayout,
    props: { isStacked: false },
  },
  [GamesModuleLayout.VerticalTilesStacked]: {
    Component: VerticalLayout,
    props: { isStacked: true },
  },
  [GamesModuleLayout.HorizontalTiles]: {
    Component: HorizontalLayout,
    props: { isStacked: false },
  },
  [GamesModuleLayout.HorizontalTilesStacked]: {
    Component: HorizontalLayout,
    props: { isStacked: true },
  },
  [GamesModuleLayout.BigHorizontalTiles]: {
    Component: BigTilesLayout,
    props: {},
  },
  [GamesModuleLayout.Top10Games]: {
    Component: NumericLayout,
    props: {},
  },
};

const SPECIAL_MODULES: Record<
  string,
  { Component: FC<{ isLoading: boolean }> }
> = {
  [ModuleType.FavouriteGames]: {
    Component: FavoriteGames,
  },
  [ModuleType.RecentlyPlayed]: {
    Component: RecentlyPlayed,
  },
};

const Games: FC<GamesProps> = ({
  isLoading,
  data,
  onEditComponent,
  onDeleteComponent,
  onUpdateStatus,
  isNewlyGenerated = false,
}) => {
  const gameIds =
    data?.selectedGames?.map((game: { id: string }) => game.id) || [];

  const hasStudioFilter = Boolean(
    data?.studioFilterEnabled && data?.orderedGamesStudiosIds.length,
  );

  const getLayoutComponent = (isNewlyGenerated: boolean) => {
    if (data.type in SPECIAL_MODULES) {
      const { Component } =
        SPECIAL_MODULES[data.type as keyof typeof SPECIAL_MODULES];
      return <Component isLoading={isLoading} />;
    }

    if (data.layout && data.layout in LAYOUT_COMPONENTS) {
      let layoutKey = data.layout;

      if (data.rows && data.rows > 1) {
        if (layoutKey === GamesModuleLayout.HorizontalTiles) {
          layoutKey = GamesModuleLayout.HorizontalTilesStacked;
        } else if (layoutKey === GamesModuleLayout.VerticalTiles) {
          layoutKey = GamesModuleLayout.VerticalTilesStacked;
        }
      }

      const { Component, props } =
        LAYOUT_COMPONENTS[layoutKey as keyof typeof LAYOUT_COMPONENTS];
      return (
        <Component
          isNewlyGenerated={isNewlyGenerated}
          gameIds={gameIds}
          isLoading={isLoading}
          {...props}
        />
      );
    }

    return null;
  };

  return (
    <ContentWrapper>
      <ModuleTileHeader
        module={data}
        onEditComponent={onEditComponent}
        onDeleteComponent={onDeleteComponent}
        isLoading={isLoading}
        onUpdateStatus={onUpdateStatus}
        isNewlyGenerated={isNewlyGenerated}
      />
      {!isLoading && hasStudioFilter && (
        <StudioFilter studioIds={data.orderedGamesStudiosIds} />
      )}
      <Box sx={{ marginTop: "16px" }}>
        {getLayoutComponent(isNewlyGenerated)}
      </Box>
    </ContentWrapper>
  );
};

export default Games;
